import {
  OperationVariables,
  QueryHookOptions,
  QueryResult,
} from '@apollo/client';
import { AutocompleteInputChangeReason } from '@mui/material';
import { Maybe } from 'graphql/jsutils/Maybe';
import { compact, find, keyBy } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { getNodes } from '@/utils/graphqlUtils';

import { TypeaheadSelectInputOption } from '../../inputTypes';
import { ValidTypeaheadValue } from '../BackendTypeaheadSelectInput';
import { BackendTypeaheadSelectProps } from '../BackendTypeaheadSelectInput';

export interface AsyncTypeaheadQuery {
  __typename?: 'Query';
  queryData?: {
    totalCount: number;
    edges?: Maybe<Maybe<{ node?: unknown }>[]>;
  };
  selectedData?: {
    totalCount: number;
    edges?: Maybe<Maybe<{ node?: unknown }>[]>;
  };
}

export type TypeaheadQueryVariables = OperationVariables;

type QueryHook<
  Query extends AsyncTypeaheadQuery,
  Variables extends TypeaheadQueryVariables,
> = (
  options: QueryHookOptions<Query, Variables>
) => QueryResult<Query, Variables>;

export type TypeaheadInputProps<V extends ValidTypeaheadValue> = Pick<
  BackendTypeaheadSelectProps<V>,
  | 'loading'
  | 'options'
  | 'emptyOption'
  | 'onInputChange'
  | 'inputValue'
  | 'popperFooter'
>;

export interface ExtendedTypeaheadQueryOpts<
  V extends ValidTypeaheadValue,
  Query extends AsyncTypeaheadQuery,
  Variables extends TypeaheadQueryVariables,
> {
  pageSize?: number;
  // Variables returned from searchTermToVariables power the full set of options for the typeahead
  searchTermToVariables: (searchTerm: string) => Partial<Variables>;
  // Variables returned from getSelectedOptionVariables power the selected option for the typeahead
  getSelectedOptionVariables: () => Partial<Variables>;
  toOptions: (
    data: CompactQueryData<Query, Variables>[]
  ) => BackendTypeaheadSelectProps<V>['options'];
  emptyOption?: TypeaheadInputProps<V>['emptyOption'];
  // If true, the input text will not be reset to an empty string when the user selects an option and the
  // autocomplete component emits a onInputUpdate event with "clear" as the reason
  skipAutocompleteResetClear?: boolean;
  // Label for grouping options that don't have a groupName. Only applied when no other options have group names.
  // This prevents mixing ungrouped options with grouped ones, which would create a visually inconsistent interface.
  unselectedGroupLabel?: string;
}

// Type to extract edges.node so we can pre-extract nested data and
// prevent needing to do a flatMap in every component that uses this
export type CompactQueryData<
  Query extends AsyncTypeaheadQuery,
  Variables extends TypeaheadQueryVariables,
> = NonNullable<
  NonNullable<
    NonNullable<
      NonNullable<
        NonNullable<QueryResult<Query, Variables>['data']>['queryData']
      >['edges']
    >[number]
  >['node']
>;

export function useAsyncTypeaheadProps<
  Query extends AsyncTypeaheadQuery,
  Variables extends TypeaheadQueryVariables,
  V extends ValidTypeaheadValue,
>(
  useQuery: QueryHook<Query, Variables>,
  {
    pageSize = 50,
    skipAutocompleteResetClear: skipResetClear = false,
    ...queryOpts
  }: QueryHookOptions<
    Query,
    // Omit internal variables first and after, these are driven by the hook and should not be provided by the consumer
    Variables
  > &
    ExtendedTypeaheadQueryOpts<V, Query, Variables>
): [
  TypeaheadInputProps<V>,
  Omit<QueryResult<Query, Variables>, 'data'> & {
    data: CompactQueryData<Query, Variables>[];
  },
] {
  const [inputText, setInputText] = useState('');
  const [lastSelectedOption, setLastSelectedOption] =
    useState<TypeaheadSelectInputOption<V> | null>(null);
  const [allOptionsByValue, setAllOptionsByValue] = useState<
    Record<string, TypeaheadSelectInputOption<V>>
  >({});
  const [suppressSearch, setSuppressSearch] = useState(false);
  const [mostRecentOptions, setMostRecentOptions] = useState<
    TypeaheadSelectInputOption<V>[]
  >([]);

  const queryArgs = useMemo<QueryHookOptions<Query, Variables>>(() => {
    const appliedSearchTerm = (() => {
      const queryIsEmptyDisplayValue =
        inputText === queryOpts.emptyOption?.display;

      if (suppressSearch) return '';
      if (queryIsEmptyDisplayValue) return '';
      return inputText;
    })();

    const variables: TypeaheadQueryVariables = {
      first: pageSize,
      ...queryOpts.variables,
      ...queryOpts.searchTermToVariables(appliedSearchTerm),
      ...queryOpts.getSelectedOptionVariables(),
    };

    return {
      fetchPolicy: 'cache-and-network',
      ...queryOpts,
      variables,
    } as QueryHookOptions<Query, Variables>;
  }, [pageSize, queryOpts, inputText, suppressSearch]);

  const queryResult = useQuery(queryArgs);
  const { data, loading: loadingQuery } = queryResult;

  // Apollo's loading variable is true for cache-and-network, even when there is a cache hit.
  // We want to only show a loading spinner when there is no cache hit, AND a fetch is occurring.
  // Context: https://github.com/apollographql/apollo-client/issues/8669#issuecomment-1464016804
  const loading = loadingQuery && !data;
  const compactData = useMemo(() => {
    const searchResultData = getNodes(data?.queryData) as CompactQueryData<
      Query,
      Variables
    >[];
    const selectedOptionsData = getNodes(
      data?.selectedData
    ) as CompactQueryData<Query, Variables>[];
    // always put the selected options first
    return [...selectedOptionsData, ...searchResultData];
  }, [data?.queryData, data?.selectedData]);
  const searchResultCount = getNodes(data?.queryData).length;
  const totalOptionCount = data?.queryData?.totalCount ?? 0;
  const formattedSearchResultCount = searchResultCount.toLocaleString('en-US');
  const formattedTotalOptionCount = totalOptionCount.toLocaleString('en-US');
  const moreOptionsFooter =
    totalOptionCount > searchResultCount
      ? `Showing ${formattedSearchResultCount} of ${formattedTotalOptionCount} options. Search to see more.`
      : undefined;

  const { toOptions } = queryOpts;
  const queryOptions = useMemo(() => {
    return toOptions(compactData);
  }, [compactData, toOptions]);

  const handleSetInputText = useCallback(
    (newSearchQuery: string, reason: AutocompleteInputChangeReason) => {
      const matchingOption = find(
        allOptionsByValue,
        (v) => v.display === newSearchQuery
      );
      if (reason === 'input') {
        setInputText(newSearchQuery);
        setSuppressSearch(false);

        matchingOption &&
          matchingOption.value !== '' &&
          setLastSelectedOption(matchingOption);
        return;
      }

      if (reason === 'reset') {
        if (newSearchQuery === queryOpts.emptyOption?.display) {
          setLastSelectedOption(null);
          setInputText('');
          setSuppressSearch(false);
          return;
        }

        matchingOption &&
          matchingOption.value !== '' &&
          setLastSelectedOption(matchingOption);

        if (skipResetClear) {
          return;
        }
      }

      if (reason === 'selectOption') {
        // Don't use the current input text (the selected option) for the next search;
        // otherwise, the user will only see options that perfectly match the currently
        // selected option!
        setSuppressSearch(true);
        // explicitly falling through to also set the input text
      }

      setInputText(newSearchQuery);
    },
    [allOptionsByValue, queryOpts.emptyOption?.display, skipResetClear]
  );

  useEffect(() => {
    const optionsByValue = keyBy(queryOptions, 'value');
    if (queryOptions.length !== 0) {
      setMostRecentOptions(queryOptions);
    }

    setAllOptionsByValue((os) => ({ ...os, ...optionsByValue }));
  }, [queryOptions]);

  const finalOptions = useMemo(() => {
    const baseOptions = compact([
      queryOpts.emptyOption,
      lastSelectedOption,
      ...mostRecentOptions,
    ]);

    // If unselectedGroupLabel is provided and no options have groupName,
    // apply the unselectedGroupLabel to all options except emptyOption and lastSelectedOption
    // (which represents the "Selected" option)
    if (queryOpts.unselectedGroupLabel) {
      const hasAnyGroupNames = baseOptions.some(
        (option) =>
          option &&
          option !== queryOpts.emptyOption &&
          option !== lastSelectedOption &&
          option.groupName
      );

      if (!hasAnyGroupNames) {
        return baseOptions.map((option) => {
          // Don't modify emptyOption or lastSelectedOption (Selected option)
          if (
            !option ||
            option === queryOpts.emptyOption ||
            option === lastSelectedOption
          ) {
            return option;
          }

          // Apply unselectedGroupLabel to options that don't have a groupName
          return option.groupName
            ? option
            : { ...option, groupName: queryOpts.unselectedGroupLabel };
        });
      }
    }

    return baseOptions;
  }, [
    lastSelectedOption,
    mostRecentOptions,
    queryOpts.emptyOption,
    queryOpts.unselectedGroupLabel,
  ]);

  const typeaheadProps: TypeaheadInputProps<V> = {
    loading,
    options: finalOptions,
    inputValue: inputText,
    onInputChange: handleSetInputText,
    popperFooter: moreOptionsFooter,
  };

  return [typeaheadProps, { ...queryResult, data: compactData }];
}
